import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, Loader2, Sparkles, Palette, Type, MessageSquare, Layers, RotateCcw, RefreshCw, Sun, Moon, Image as ImageIcon, Plus, X, FileText, Film, Upload, Link, Globe, ExternalLink, Monitor, Smartphone, Lock, Save, Check, Search } from 'lucide-react';
import axios from 'axios';
import { useToast, ToastContainer } from '../ui/Toast';
import EmbeddedCheckoutModal from '../ui/EmbeddedCheckout';
import PricingModal from '../ui/PricingModal';
import SEOAudit from './SEOAudit';
import CopyQuestionnaire from './CopyQuestionnaire';
import { FONT_PAIRINGS, PAGE_TYPES, SECTION_SUGGESTIONS } from './constants';
import BuilderTopbar from './BuilderTopbar';
import BuilderPreview from './BuilderPreview';
import BuilderSidebar from './BuilderSidebar';
import BuilderOnboarding from './BuilderOnboarding';
import BuilderV2Layout from './BuilderV2Layout';
import './builder-v2.css';
import SectionsTab from './tabs/SectionsTab';
import MediaTab from './tabs/MediaTab';
import ColorsTab from './tabs/ColorsTab';
import FontsTab from './tabs/FontsTab';
import ChatTab from './tabs/ChatTab';
import BlogTab from './tabs/BlogTab';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;


const BuilderPage = ({ user }) => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(null);
  const [sections, setSections] = useState([]);
  const [colors, setColors] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [sectionChat, setSectionChat] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('sections');
  const [fullChat, setFullChat] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [seenSuggestions, setSeenSuggestions] = useState({});
  const [currentSuggestion, setCurrentSuggestion] = useState({});
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [theme, setTheme] = useState('light');
  const [showTips, setShowTips] = useState(false);
  const [showDomain, setShowDomain] = useState(false);
  const [domainConfig, setDomainConfig] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [publishStatus, setPublishStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showSEO, setShowSEO] = useState(false);
  const [showCopyQ, setShowCopyQ] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingYearly, setPricingYearly] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutPlanKey, setCheckoutPlanKey] = useState(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [newPageType, setNewPageType] = useState('about');
  const [addingPage, setAddingPage] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [swappingMedia, setSwappingMedia] = useState(null); // index of media being swapped
  const [mediaVideoUrl, setMediaVideoUrl] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showGalleryEmbed, setShowGalleryEmbed] = useState(false);
  const [galleryUrl, setGalleryUrl] = useState('');
  const [galleryTitle, setGalleryTitle] = useState('Client Gallery');
  const [addingGallery, setAddingGallery] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenDirection, setRegenDirection] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [publishCelebration, setPublishCelebration] = useState(null); // { url } | null
  const autosaveTimer = useRef(null);
  const { toast: toastData, confirm: confirmData, showToast, showConfirm, handleConfirm, setToast } = useToast();

  const handleSave = useCallback(async () => {
    try {
      await axios.patch(`${API}/sites/${siteId}`, { html: site?.pages?.find(p => p.id === activePage)?.html, page_id: activePage }, { withCredentials: true });
      setSaveStatus('saved'); setHasUnsavedChanges(false); setTimeout(() => setSaveStatus(null), 2000);
    } catch { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 3000); }
  }, [siteId, site, activePage]);

  // Autosave: save 3 seconds after last change
  const triggerAutosave = useCallback(() => {
    setHasUnsavedChanges(true);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      handleSave();
    }, 3000);
  }, [handleSave]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => { window.removeEventListener('beforeunload', handler); if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [hasUnsavedChanges]);

  // handleUndo defined before use in keyboard shortcuts
  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(s => s.slice(0, -1));
    try {
      await axios.patch(`${API}/sites/${siteId}`,
        { html: prev.html, page_id: prev.pageId },
        { withCredentials: true }
      );
      setSite(s => ({
        ...s,
        pages: s.pages.map(p => p.id === prev.pageId ? { ...p, html: prev.html } : p),
        version: (s.version || 1) + 1,
      }));
    } catch { showToast("Iris couldn't undo that change. The version may have expired.", "error"); }
  }, [undoStack, siteId, showToast]);

  // Keyboard shortcuts: Cmd+Z (undo), Cmd+S (save)
  useEffect(() => {
    const handler = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (undoStack.length > 0) handleUndo();
      }
      if (isMeta && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undoStack, handleUndo, handleSave]);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Track active page HTML for autosave — skip initial load
  const initialHtmlRef = useRef(null);
  useEffect(() => {
    if (!site) return;
    const currentHtml = site?.pages?.find(p => p.id === activePage)?.html;
    if (!currentHtml) return;
    if (initialHtmlRef.current === null) { initialHtmlRef.current = currentHtml; return; }
    if (currentHtml !== initialHtmlRef.current) { triggerAutosave(); }
  }, [site, activePage, triggerAutosave]);

  // Theme CSS vars
  const t = useMemo(() => theme === 'light' ? {
    bg: '#faf8f5', fg: '#1a1917', muted: 'rgba(26,25,23,0.45)', faint: 'rgba(26,25,23,0.08)',
    gold: '#C9A84C', cardBg: '#ffffff', inputBg: '#f5f2ee', border: 'rgba(26,25,23,0.08)',
    chatUser: '#C9A84C', chatUserText: '#fff', chatAi: '#f5f2ee', chatAiText: '#1a1917',
    tabActive: '#1a1917', tabInactive: 'rgba(26,25,23,0.35)',
    sidebar: '#f5f2ee', surface: '#ffffff',
  } : {
    bg: '#121110', fg: '#f2efe9', muted: 'rgba(242,239,233,0.45)', faint: 'rgba(242,239,233,0.07)',
    gold: 'var(--gold)', cardBg: '#1a1816', inputBg: '#1e1c19', border: 'rgba(242,239,233,0.06)',
    chatUser: 'var(--gold)', chatUserText: '#000', chatAi: '#1a1816', chatAiText: '#f2efe9',
    tabActive: 'var(--gold)', tabInactive: 'rgba(242,239,233,0.35)',
    sidebar: '#151311', surface: '#1a1816',
  }, [theme]);

  const inputStyle = useMemo(() => ({
    width: '100%', padding: '10px 14px', fontSize: '13px', fontFamily: 'var(--sans)',
    background: t.inputBg,
    border: `1px solid ${t.border}`,
    color: t.fg, outline: 'none', borderRadius: '8px',
  }), [t]);

  // Active page data
  const activePageData = site?.pages?.find(p => p.id === activePage);
  const activeHtml = activePageData?.html || '';

  useEffect(() => { fetchSite(); }, [siteId]); // eslint-disable-line

  const fetchSite = async () => {
    try {
      const res = await axios.get(`${API}/sites/${siteId}`, { withCredentials: true });
      const s = res.data.site;
      setSite(s);
      setChatHistory(s.chat_history || []);
      if (s.pages?.length) {
        const home = s.pages.find(p => p.is_home) || s.pages[0];
        setActivePage(home.id);
        await loadSections(siteId, home.id);
        await loadMedia(home.id);
      }
      // Load domain config and usage stats
      setDomainConfig(s.custom_domain || null);
      try {
        const usageRes = await axios.get(`${API}/sites/${siteId}/usage`, { withCredentials: true });
        setUsageStats(usageRes.data);
      } catch { /* ignore */ }
      try {
        const limitsRes = await axios.get(`${API}/billing/limits`, { withCredentials: true });
        setUserPlan(limitsRes.data);
      } catch { /* ignore */ }
      fetchPublishStatus();
      // Fetch suggestions for Iris
      try {
        const sugRes = await axios.get(`${API}/sites/${siteId}/suggestions`, { withCredentials: true });
        setSiteState(sugRes.data.site_state || {});
        if (sugRes.data.iris_greeting && chatHistory.length === 0) {
          setChatHistory([{ role: 'assistant', message: sugRes.data.iris_greeting, suggestions: sugRes.data.suggestions?.slice(0, 3).map(s => ({ label: s.label, action: s.action })) }]);
        }
      } catch { /* ignore */ }
    } catch { navigate('/auth'); }
    setLoading(false);
  };

  const loadSections = useCallback(async (id, pageId) => {
    const pid = pageId || activePage;
    if (!pid) return;
    try {
      const res = await axios.get(`${API}/sites/${id || siteId}/sections?page_id=${pid}`, { withCredentials: true });
      setSections(res.data.sections);
      setColors(res.data.colors);
      setFonts(res.data.fonts);
    } catch { /* sections couldn't be parsed */ }
  }, [siteId, activePage]);

  const loadMedia = useCallback(async (pageId) => {
    const pid = pageId || activePage;
    if (!pid) return;
    try {
      const res = await axios.get(`${API}/sites/${siteId}/media?page_id=${pid}`, { withCredentials: true });
      setMediaItems(res.data.media || []);
    } catch { setMediaItems([]); }
  }, [siteId, activePage]);

  const reloadPageData = useCallback(async (pageId) => {
    const pid = pageId || activePage;
    await loadSections(siteId, pid);
    await loadMedia(pid);
  }, [siteId, activePage, loadSections, loadMedia]);

  const handleMediaSwap = async (oldUrl, newUrl) => {
    if (!newUrl || newUrl === oldUrl) { setSwappingMedia(null); return; }
    saveUndo();
    try {
      const res = await axios.post(`${API}/sites/${siteId}/media/swap`,
        { old_url: oldUrl, new_url: newUrl, page_id: activePage },
        { withCredentials: true }
      );
      setSite(prev => ({
        ...prev,
        pages: prev.pages.map(p => p.id === activePage ? { ...p, html: res.data.html } : p),
        version: res.data.version,
      }));
      await loadMedia(activePage);
      setSwappingMedia(null);
      setMediaVideoUrl('');
    } catch (err) {
      const msg = err.response?.data?.detail;
      showToast(typeof msg === 'object' ? msg.message : msg || 'Iris couldn\'t swap that media. Try again.', 'error');
    }
  };

  const handleMediaUploadAndSwap = async (file, oldUrl) => {
    const formData = new FormData();
    formData.append('files', file);
    try {
      const upRes = await axios.post(`${API}/sites/${siteId}/media/upload`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      if (upRes.data.urls?.length > 0) {
        await handleMediaSwap(oldUrl, upRes.data.urls[0]);
      }
    } catch {
      showToast('Iris couldn\'t upload that file. Make sure it\'s an image under 15MB.', 'error');
    }
  };

  const switchPage = async (pageId) => {
    setActivePage(pageId);
    setActiveSection(null);
    setSwappingMedia(null);
    await loadSections(siteId, pageId);
    await loadMedia(pageId);
  };

  const handleConnectDomain = async () => {
    if (userPlan && userPlan.plan !== 'pro') {
      setShowPricing(true);
      return;
    }
    if (!domainInput.trim() || domainSaving) return;
    setDomainSaving(true);
    try {
      const res = await axios.post(`${API}/sites/${siteId}/domain`, { domain: domainInput.trim() }, { withCredentials: true });
      setDomainConfig(res.data.domain);
      setDomainInput('');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Iris couldn\'t connect the domain. Try again.', 'error');
    }
    setDomainSaving(false);
  };

  const handleRemoveDomain = async () => {
    const ok = await showConfirm('Remove this custom domain? Your site will only be available at its goldenhour.build subdomain.');
    if (!ok) return;
    try {
      await axios.delete(`${API}/sites/${siteId}/domain`, { withCredentials: true });
      setDomainConfig(null);
    } catch {
      showToast('Couldn\'t remove the domain. Try again.', 'error');
    }
  };

  const fetchPublishStatus = async () => {
    try {
      const res = await axios.get(`${API}/sites/${siteId}/publish/status`, { withCredentials: true });
      setPublishStatus(res.data);
    } catch { /* not published */ }
  };

  const handlePublish = async () => {
    // Check plan first
    if (userPlan && userPlan.plan === 'free') {
      setShowPricing(true);
      return;
    }
    // Publish confirmation
    const subdomain = site?.business_name?.toLowerCase().replace(/\s+/g, '').replace(/&/g, 'and').replace(/[^a-z0-9]/g, '') || siteId.slice(0, 8);
    const ok = await showConfirm(
      `Publish your site? It will be live at ${subdomain}.goldenhour.build and visible to anyone with the link.`
    );
    if (!ok) return;

    setPublishing(true);
    try {
      const res = await axios.post(`${API}/sites/${siteId}/publish`, {}, { withCredentials: true });
      setPublishStatus(res.data);
      const liveUrl = res.data?.urls?.subdomain_url || `${subdomain}.goldenhour.build`;
      setPublishCelebration({ url: liveUrl });
      // Auto-dismiss after 6 seconds
      setTimeout(() => setPublishCelebration(null), 6000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Iris couldn\'t publish right now. Try again.';
      if (err.response?.status === 403) {
        setShowPricing(true);
      } else {
        showToast(typeof detail === 'string' ? detail : detail.message || 'Publishing failed.', 'error');
      }
    }
    setPublishing(false);
  };

  const handleUnpublish = async () => {
    const ok = await showConfirm('Take this site offline? Visitors will see a "not found" page.');
    if (!ok) return;
    try {
      await axios.post(`${API}/sites/${siteId}/unpublish`, {}, { withCredentials: true });
      setPublishStatus({ status: 'offline', urls: null });
    } catch (err) {
      const d = err.response?.data?.detail;
      showToast(typeof d === 'object' ? d.message : d || 'Couldn\'t take the site offline. Try again.', 'error');
    }
  };

  const handleCheckout = async (planKey) => {
    setCheckoutPlanKey(planKey);
    setShowPricing(false);
  };

  const handleCheckoutSuccess = async () => {
    setCheckoutPlanKey(null);
    // Refresh plan info
    try {
      const limitsRes = await axios.get(`${API}/billing/limits`, { withCredentials: true });
      setUserPlan(limitsRes.data);
    } catch {}
    showToast('Welcome to GoldenHour! Iris is ready to help you build something incredible.', 'success');
  };


  const saveUndo = () => {
    if (activeHtml && activePage) {
      setUndoStack(prev => [...prev.slice(-19), { pageId: activePage, html: activeHtml }]);
    }
  };

  const handleAddPage = async () => {
    if (!newPageName.trim() || addingPage) return;
    setAddingPage(true);
    try {
      const res = await axios.post(`${API}/sites/${siteId}/pages`,
        { name: newPageName.trim(), page_type: newPageType },
        { withCredentials: true, timeout: 120000 }
      );
      const newPage = res.data.page;
      // Use the full synced pages list if returned (nav links updated across all pages)
      const syncedPages = res.data.pages || [...(site.pages || []), newPage];
      setSite(s => ({ ...s, pages: syncedPages, version: (s.version || 1) + 1 }));
      setShowAddPage(false);
      setNewPageName('');
      setNewPageType('about');
      setActivePage(newPage.id);
      await loadSections(siteId, newPage.id);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Iris couldn\'t generate the page. Give it another shot.', 'error');
    }
    setAddingPage(false);
  };

  const handleDeletePage = async (pageId) => {
    const ok = await showConfirm('Delete this page? This can\'t be undone.');
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/sites/${siteId}/pages/${pageId}`, { withCredentials: true });
      const syncedPages = res.data.pages || site.pages.filter(p => p.id !== pageId);
      setSite(s => ({ ...s, pages: syncedPages }));
      if (activePage === pageId) {
        const home = syncedPages.find(p => p.is_home) || syncedPages[0];
        if (home) switchPage(home.id);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Couldn\'t delete this page.', 'error');
    }
  };

  const handleAddGallery = async () => {
    if (!galleryUrl.trim() || addingGallery) return;
    setAddingGallery(true);
    saveUndo();
    try {
      const res = await axios.post(`${API}/sites/${siteId}/gallery`,
        { url: galleryUrl.trim(), title: galleryTitle.trim() || 'Client Gallery', page_id: activePage },
        { withCredentials: true }
      );
      setSite(s => {
        const pages = (s.pages || []).map(p => p.id === activePage ? { ...p, html: res.data.html } : p);
        return { ...s, pages, version: res.data.version };
      });
      await reloadPageData(activePage);
      setShowGalleryEmbed(false);
      setGalleryUrl('');
      setGalleryTitle('Client Gallery');
      showToast(`${res.data.provider} gallery embedded!`, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Couldn\'t embed gallery.', 'error');
    }
    setAddingGallery(false);
  };

  const fetchSuggestion = async (sectionType, sectionId, sectionLabel) => {
    const pool = SECTION_SUGGESTIONS[sectionType] || SECTION_SUGGESTIONS.content;
    const seen = seenSuggestions[sectionId] || new Set();
    let unseen = null;
    for (let i = 0; i < pool.length; i++) {
      if (!seen.has(i)) { unseen = i; break; }
    }
    if (unseen !== null) {
      const newSeen = new Set(seen);
      newSeen.add(unseen);
      setSeenSuggestions(prev => ({ ...prev, [sectionId]: newSeen }));
      setCurrentSuggestion(prev => ({ ...prev, [sectionId]: pool[unseen] }));
    } else {
      setLoadingSuggestion(true);
      try {
        const res = await axios.post(`${API}/sites/${siteId}/suggest`,
          { section_type: sectionType, section_label: sectionLabel },
          { withCredentials: true }
        );
        setCurrentSuggestion(prev => ({ ...prev, [sectionId]: res.data.suggestion }));
      } catch {
        setSeenSuggestions(prev => ({ ...prev, [sectionId]: new Set() }));
        setCurrentSuggestion(prev => ({ ...prev, [sectionId]: pool[0] }));
      }
      setLoadingSuggestion(false);
    }
  };

  const handleSectionChat = async (e) => {
    e.preventDefault();
    if (!sectionChat.trim() || !activeSection || sending) return;
    saveUndo();
    setSending(true);
    const msg = sectionChat.trim();
    setSectionChat('');
    try {
      const res = await axios.post(`${API}/sites/${siteId}/sections/${activeSection}/chat`,
        { message: msg, section_id: activeSection, page_id: activePage },
        { withCredentials: true, timeout: 120000 }
      );
      // Only update preview if there was an actual edit
      if (!res.data.no_edit) {
        setSite(prev => ({
          ...prev,
          html: res.data.html,
          pages: prev.pages.map(p => p.id === activePage ? { ...p, html: res.data.html } : p),
          version: res.data.version,
        }));
        await reloadPageData(activePage);
      }
      // Add to chat history with AI message
      if (res.data.ai_message) {
        const section = sections.find(s => s.id === activeSection);
        setChatHistory(prev => [
          ...prev,
          { role: 'user', message: `[${section?.label || 'Section'}] ${msg}` },
          { role: 'assistant', message: res.data.ai_message },
        ]);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'object' ? d.message : d || 'Iris ran into an issue with that edit. Try again.';
      showToast(msg, 'error');
      if (d?.upgrade_required) {
        setChatHistory(prev => [...prev, { role: 'assistant', message: msg + ' Choose a plan in Settings → Billing to unlock Iris editing.' }]);
      }
    }
    setSending(false);
  };

  const handleFullChat = async (msgOrEvent) => {
    const msg = typeof msgOrEvent === 'string' ? msgOrEvent : fullChat.trim();
    if (!msg || sending) return;
    if (typeof msgOrEvent !== 'string') msgOrEvent.preventDefault();
    saveUndo();
    setFullChat('');
    setSending(true);

    setChatHistory(prev => [...prev, { role: 'user', message: msg }]);

    try {
      // Use SSE streaming endpoint for progressive text display
      const payload = { message: msg, page_id: activePage };
      const response = await fetch(`${API}/sites/${siteId}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const detail = errData.detail;
        const errMsg = typeof detail === 'object' ? detail.message : detail;
        if (response.status === 403 || response.status === 429) {
          setChatHistory(prev => [...prev, { role: 'assistant', message: errMsg || 'Upgrade required.' }]);
          setShowPricing(true);
        } else {
          setChatHistory(prev => [...prev, { role: 'assistant', message: errMsg || 'Iris hit a snag — try again.' }]);
        }
        setSending(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      let streamMsgAdded = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'text') {
              streamedText += data.content;
              // Update the streaming message in chat history
              if (!streamMsgAdded) {
                setChatHistory(prev => [...prev, { role: 'assistant', message: streamedText, _streaming: true }]);
                streamMsgAdded = true;
              } else {
                setChatHistory(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant' && updated[lastIdx]?._streaming) {
                    updated[lastIdx] = { ...updated[lastIdx], message: streamedText };
                  }
                  return updated;
                });
              }
            } else if (data.type === 'status') {
              // Update streaming message with status
              if (streamMsgAdded) {
                setChatHistory(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant' && updated[lastIdx]?._streaming) {
                    updated[lastIdx] = { ...updated[lastIdx], message: streamedText + '\n\n*' + data.content + '*' };
                  }
                  return updated;
                });
              }
            } else if (data.type === 'done') {
              // Final event — update preview and finalize chat
              if (!data.no_edit && data.html) {
                setSite(prev => ({
                  ...prev,
                  pages: prev.pages.map(p => p.id === activePage ? { ...p, html: data.html } : p),
                  version: data.version,
                }));
                await reloadPageData(activePage);
              }
              const finalMessage = data.ai_message || streamedText || 'Done!';
              setChatHistory(prev => {
                const updated = prev.filter(m => !m._streaming);
                const editCount = updated.filter(m => m.role === 'user').length;
                const followUp = { role: 'assistant', message: finalMessage };
                if (editCount >= 5 && !publishStatus?.status?.includes('live')) {
                  followUp.message += '\n\nYour site is looking really polished. Ready to go live?';
                  followUp.suggestions = [
                    { label: 'Publish my site', action: 'publish' },
                    { label: 'Keep editing', action: 'continue' },
                  ];
                }
                return [...updated, followUp];
              });
            } else if (data.type === 'error') {
              setChatHistory(prev => {
                const updated = prev.filter(m => !m._streaming);
                return [...updated, { role: 'assistant', message: data.content }];
              });
            }
          } catch { /* skip malformed lines */ }
        }
      }
    } catch (err) {
      setChatHistory(prev => {
        const updated = prev.filter(m => !m._streaming);
        return [...updated, { role: 'assistant', message: 'Iris hit a snag — give it another shot and I\'ll sort it out.' }];
      });
    }
    setSending(false);
  };

  const handleColorSwap = async (oldColor, newColor) => {
    saveUndo();
    try {
      const res = await axios.post(`${API}/sites/${siteId}/swap-color`,
        { old_color: oldColor, new_color: newColor, page_id: activePage },
        { withCredentials: true }
      );
      setSite(prev => ({
        ...prev,
        pages: prev.pages.map(p => p.id === activePage ? { ...p, html: res.data.html } : p),
        version: res.data.version,
      }));
      await reloadPageData(activePage);
    } catch { showToast("Iris couldn't swap that color. Try again.", "error"); }
  };

  const handleFontSwap = async (oldFont, newFont) => {
    saveUndo();
    try {
      const res = await axios.post(`${API}/sites/${siteId}/swap-font`,
        { old_font: oldFont, new_font: newFont, page_id: activePage },
        { withCredentials: true }
      );
      setSite(prev => ({
        ...prev,
        pages: prev.pages.map(p => p.id === activePage ? { ...p, html: res.data.html } : p),
        version: res.data.version,
      }));
      await reloadPageData(activePage);
    } catch { showToast("Iris couldn't swap that font. Try again.", "error"); }
  };

  // Site state tracking for Iris suggestions
  const [siteState, setSiteState] = useState({});

  if (loading) return (
    <div className="builder-v2" style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#faf8f5' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,168,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', animation: 'builderPulse 2s ease-in-out infinite' }}>
        <Sparkles size={22} style={{ color: '#C9A84C' }} />
      </div>
      <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', color: '#2a2725', fontWeight: 500 }}>Loading your site...</p>
    </div>
  );
  if (!site) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#faf8f5', fontFamily: 'var(--sans)', padding: '24px', textAlign: 'center' }}>
      <p style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: '#1F1D1B', marginBottom: '16px', letterSpacing: '-.02em' }}>
        golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
      </p>
      <p style={{ fontSize: '16px', fontWeight: 600, color: '#1F1D1B', marginBottom: '8px' }}>Site not found</p>
      <p style={{ fontSize: '14px', color: '#8c8782', marginBottom: '24px' }}>This site may have been deleted, or you may not have access to it.</p>
      <a href="/dashboard" style={{ padding: '12px 28px', background: '#C9A84C', color: '#fff', textDecoration: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600 }}>Go to Dashboard</a>
    </div>
  );

  const handleReorderSection = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const newOrder = [...sections];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    setSections(newOrder);
    saveUndo();
    try {
      const sectionIds = newOrder.map(s => s.id);
      await axios.post(`${API}/sites/${siteId}/reorder-sections`, { section_ids: sectionIds, page_id: activePage }, { withCredentials: true });
      await reloadPageData(activePage);
    } catch {
      showToast('Couldn\'t reorder. Try again.', 'error');
    }
  };

  const handleRegenerate = async () => {
    setShowRegenConfirm(false);
    setRegenerating(true);
    saveUndo();
    const direction = regenDirection.trim();
    setRegenDirection('');
    try {
      const res = await axios.post(`${API}/demo/generate`, {
        business_name: site.business_name || 'My Studio',
        business_type: site.business_type || 'photographer',
        style: 'romantic',
        notes: direction || 'Generate a completely fresh, unique design — different concept, different layout, different color palette.',
        tier: 'premium',
      }, { withCredentials: true });
      const jobId = res.data.job_id;
      const poll = async () => {
        try {
          const s = await axios.get(`${API}/demo/status/${jobId}`);
          if (s.data.status === 'complete') {
            await axios.patch(`${API}/sites/${siteId}`, { html: s.data.html, page_id: activePage }, { withCredentials: true });
            setSite(prev => ({ ...prev, pages: prev.pages.map(p => p.id === activePage ? { ...p, html: s.data.html } : p), version: (prev.version || 1) + 1 }));
            await reloadPageData(activePage);
            setRegenerating(false);
            showToast('New design ready! Use Undo if you prefer the previous version.', 'success');
            try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); const n = (f,s,d) => { const o=ctx.createOscillator(); const g=ctx.createGain(); o.type='sine'; o.frequency.value=f; g.gain.setValueAtTime(0.15,ctx.currentTime+s); g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+s+d); o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime+s); o.stop(ctx.currentTime+s+d); }; n(523,0,0.15); n(659,0.12,0.15); n(784,0.24,0.3); } catch {}
          } else if (s.data.status === 'failed') {
            setRegenerating(false);
            showToast(s.data.error || 'Regeneration failed. Try again.', 'error');
          } else {
            setTimeout(poll, 3000);
          }
        } catch { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setRegenerating(false);
      showToast(e.response?.data?.detail?.message || 'Couldn\'t start regeneration.', 'error');
    }
  };

  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#faf8f5', padding: '32px', textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: '#1F1D1B', marginBottom: '24px', letterSpacing: '-.02em' }}>
          golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
        </p>
        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(201,168,76,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Monitor size={24} style={{ color: '#C9A84C' }} />
        </div>
        <h2 style={{ fontFamily: 'var(--sans)', fontSize: '20px', fontWeight: 600, color: '#1F1D1B', marginBottom: '8px' }}>Builder works best on desktop</h2>
        <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: '#8c8782', lineHeight: 1.6, maxWidth: '320px', marginBottom: '28px' }}>
          Iris needs a bigger screen to show your site preview and chat side by side. Open this link on your laptop or desktop for the best experience.
        </p>
        <a href="/dashboard" style={{ padding: '12px 28px', background: '#C9A84C', color: '#fff', textDecoration: 'none', borderRadius: '10px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600 }}>
          Go to Dashboard
        </a>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toast={toastData} confirm={confirmData} onConfirm={handleConfirm} onDismiss={() => setToast(null)} />

      {/* Publish celebration */}
      {publishCelebration && (
        <div data-testid="publish-celebration" onClick={() => setPublishCelebration(null)} style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(250,248,245,0.97)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', animation: 'fadeIn 0.4s ease',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '440px', padding: '0 24px' }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'rgba(91,168,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px', animation: 'scalePop 0.6s cubic-bezier(.25,.46,.45,.94)',
            }}>
              <Globe size={32} style={{ color: '#5ba89d' }} />
            </div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '32px', color: '#1F1D1B', lineHeight: 1.15, marginBottom: '12px', letterSpacing: '-.02em' }}>
              You're <em style={{ fontStyle: 'italic', color: '#C9A84C' }}>live.</em>
            </h2>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', color: '#6B6660', lineHeight: 1.7, marginBottom: '24px' }}>
              Your site is published and ready for the world. Share it with your clients.
            </p>
            <a href={publishCelebration.url.startsWith('http') ? publishCelebration.url : `https://${publishCelebration.url}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="publish-celebration-url"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '14px 28px', background: '#C9A84C', color: '#fff',
                textDecoration: 'none', borderRadius: '10px', fontFamily: 'var(--sans)',
                fontSize: '14px', fontWeight: 600, boxShadow: '0 4px 12px rgba(201,168,76,0.2)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <Globe size={16} /> Visit Your Site
            </a>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#bfb9b3', marginTop: '16px' }}>
              Tap anywhere to dismiss
            </p>
          </div>
        </div>
      )}

      {/* V2 Chat-First Layout — onboarding renders inline in the chat panel */}
      <BuilderV2Layout
        site={site} siteId={siteId} activePageData={activePageData} activeHtml={activeHtml} activePage={activePage}
        navigate={navigate} pages={site?.pages || []}
        chatHistory={chatHistory} sending={sending} handleFullChat={handleFullChat} setChatHistory={setChatHistory}
        handleSave={handleSave} saveStatus={saveStatus} hasUnsavedChanges={hasUnsavedChanges}
        handlePublish={handlePublish} publishing={publishing}
        publishStatus={publishStatus} handleUndo={handleUndo} undoStack={undoStack}
        setShowRegenConfirm={setShowRegenConfirm} regenerating={regenerating}
        previewMode={previewMode} setPreviewMode={setPreviewMode} setActivePage={setActivePage}
        setShowSEO={setShowSEO} setShowCopyQ={setShowCopyQ} setShowPricing={setShowPricing}
        userPlan={userPlan} siteState={siteState} usageStats={usageStats}
        mediaItems={mediaItems} onMediaSwap={handleMediaSwap} onMediaUpload={handleMediaUploadAndSwap}
        colors={colors} onColorSwap={handleColorSwap}
        fonts={fonts} onFontSwap={handleFontSwap}
        isOnboarding={site && !site.onboarding_complete}
        onOnboardingComplete={(action) => { setSite(prev => ({ ...prev, onboarding_complete: true })); if (action === 'published') { axios.get(`${API}/sites/${siteId}/publish/status`, { withCredentials: true }).then(r => setPublishStatus(r.data)).catch(() => {}); } fetchSite(); }}
      />

      {/* ── Pricing Modal ── */}
      {showPricing && (
        <PricingModal
          onSelectPlan={(planKey) => { handleCheckout(planKey); }}
          onClose={() => !checkingOut && setShowPricing(false)}
          subtitle="Your site preview is saved. Choose a plan to start customizing."
          isLoggedIn={true}
        />
      )}

      {/* ── Embedded Stripe Checkout ── */}
      {checkoutPlanKey && (
        <EmbeddedCheckoutModal
          planKey={checkoutPlanKey}
          returnPath={`/builder/${siteId}`}
          onClose={() => setCheckoutPlanKey(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {/* ── SEO Audit Modal ── */}
      {showSEO && (
        <SEOAudit
          siteId={siteId}
          siteName={site?.business_name}
          onClose={() => setShowSEO(false)}
          onOpenBlogTab={() => setTab('blog')}
          onSiteUpdated={async () => {
            const res = await axios.get(`${API}/sites/${siteId}`, { withCredentials: true });
            setSite(res.data.site || res.data);
          }}
        />
      )}
      {/* ── Copy Questionnaire Modal ── */}
      {showCopyQ && (
        <CopyQuestionnaire
          siteId={siteId}
          siteName={site?.business_name}
          onClose={() => setShowCopyQ(false)}
          onSiteUpdated={async () => {
            const res = await axios.get(`${API}/sites/${siteId}`, { withCredentials: true });
            setSite(res.data.site || res.data);
          }}
        />
      )}
      {/* ── Add Page Modal ── */}
      {showAddPage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => !addingPage && setShowAddPage(false)}>
          <div data-testid="add-page-modal" onClick={e => e.stopPropagation()} style={{
            background: theme === 'light' ? '#fff' : '#1a1917',
            border: `1px solid ${t.border}`,
            padding: '32px', maxWidth: '420px', width: '90%',
            boxShadow: '0 40px 100px rgba(0,0,0,.5)', borderRadius: '12px',
          }}>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: '22px', marginBottom: '4px', color: t.fg }}>Add a New Page</h3>
            <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: t.muted, letterSpacing: '.06em', marginBottom: '28px' }}>
              AI will craft it to match your site's look and feel
            </p>

            <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: t.muted, display: 'block', marginBottom: '6px' }}>Page Name</label>
            <input
              type="text" value={newPageName} onChange={e => setNewPageName(e.target.value)}
              placeholder="About"
              disabled={addingPage}
              data-testid="new-page-name-input"
              style={{ ...inputStyle, marginBottom: '20px' }}
              autoFocus
            />

            <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: t.muted, display: 'block', marginBottom: '6px' }}>Page Type</label>
            <select
              value={newPageType} onChange={e => setNewPageType(e.target.value)}
              disabled={addingPage}
              data-testid="new-page-type-select"
              style={{ ...inputStyle, marginBottom: '28px', cursor: 'pointer' }}
            >
              {PAGE_TYPES.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>

            {addingPage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px', background: theme === 'light' ? 'rgba(138,112,48,0.05)' : 'rgba(196,162,101,0.04)', border: `1px solid ${theme === 'light' ? 'rgba(138,112,48,0.1)' : 'rgba(196,162,101,0.08)'}` }}>
                <Loader2 size={14} className="animate-spin" style={{ color: t.gold, flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.fg }}>
                  Iris is crafting your {newPageName || 'new'} page... This usually takes about 30 seconds.
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowAddPage(false); setNewPageName(''); }}
                disabled={addingPage}
                style={{
                  flex: 1, padding: '10px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em',
                  textTransform: 'uppercase', cursor: addingPage ? 'default' : 'pointer',
                  background: 'transparent', border: `1px solid ${t.border}`, color: t.fg,
                  opacity: addingPage ? 0.4 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPage}
                disabled={!newPageName.trim() || addingPage}
                data-testid="generate-page-btn"
                style={{
                  flex: 1, padding: '10px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em',
                  textTransform: 'uppercase', cursor: (!newPageName.trim() || addingPage) ? 'default' : 'pointer',
                  background: (!newPageName.trim() || addingPage) ? t.faint : t.gold,
                  border: 'none', color: (!newPageName.trim() || addingPage) ? t.muted : (theme === 'light' ? '#fff' : '#000'),
                }}
              >
                {addingPage ? 'Generating...' : 'Generate Page'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Gallery Embed Modal ── */}
      {showGalleryEmbed && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => !addingGallery && setShowGalleryEmbed(false)}>
          <div data-testid="gallery-embed-modal" onClick={e => e.stopPropagation()} style={{
            background: theme === 'light' ? '#fff' : '#1a1917',
            border: `1px solid ${t.border}`,
            padding: '32px', maxWidth: '480px', width: '90%',
            boxShadow: '0 40px 100px rgba(0,0,0,.5)', borderRadius: '12px',
          }}>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: '22px', marginBottom: '4px', color: t.fg }}>Embed Client Gallery</h3>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.muted, lineHeight: 1.6, marginBottom: '24px' }}>
              Paste your gallery URL and we'll embed it beautifully into your site.
            </p>

            <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: t.muted, display: 'block', marginBottom: '6px' }}>Gallery URL</label>
            <input
              type="url" value={galleryUrl} onChange={e => setGalleryUrl(e.target.value)}
              placeholder="https://your-gallery.pic-time.com/sarah-and-james"
              disabled={addingGallery}
              data-testid="gallery-url-input"
              style={{ ...inputStyle, marginBottom: '16px' }}
              autoFocus
            />

            <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: t.muted, display: 'block', marginBottom: '6px' }}>Section Title</label>
            <input
              type="text" value={galleryTitle} onChange={e => setGalleryTitle(e.target.value)}
              placeholder="Client Gallery"
              disabled={addingGallery}
              data-testid="gallery-title-input"
              style={{ ...inputStyle, marginBottom: '20px' }}
            />

            {/* Supported providers */}
            <div style={{ marginBottom: '24px', padding: '12px', background: 'rgba(196,162,101,0.03)', border: `1px solid ${t.border}` }}>
              <p style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.1em', textTransform: 'uppercase', color: t.gold, marginBottom: '8px' }}>Supported Platforms</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {['Pic-Time', 'Pixieset', 'ShootProof', 'CloudSpot', 'Pass', 'SmugMug'].map(p => (
                  <span key={p} style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: t.muted, padding: '3px 8px', background: 'rgba(196,162,101,0.06)', borderRadius: '4px' }}>{p}</span>
                ))}
              </div>
            </div>

            {addingGallery && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px', background: 'rgba(196,162,101,0.04)', border: '1px solid rgba(196,162,101,0.08)' }}>
                <Loader2 size={14} className="animate-spin" style={{ color: t.gold, flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.fg }}>Embedding your gallery...</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowGalleryEmbed(false); setGalleryUrl(''); setGalleryTitle('Client Gallery'); }}
                disabled={addingGallery}
                style={{
                  flex: 1, padding: '10px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em',
                  textTransform: 'uppercase', cursor: addingGallery ? 'default' : 'pointer',
                  background: 'transparent', border: `1px solid ${t.border}`, color: t.fg,
                  opacity: addingGallery ? 0.4 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddGallery}
                disabled={!galleryUrl.trim() || addingGallery}
                data-testid="embed-gallery-submit"
                style={{
                  flex: 1, padding: '10px', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.08em',
                  textTransform: 'uppercase', cursor: (!galleryUrl.trim() || addingGallery) ? 'default' : 'pointer',
                  background: (!galleryUrl.trim() || addingGallery) ? t.faint : t.gold,
                  border: 'none', color: (!galleryUrl.trim() || addingGallery) ? t.muted : (theme === 'light' ? '#fff' : '#000'),
                }}
              >
                {addingGallery ? 'Embedding...' : 'Embed Gallery'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Regenerate Confirmation ── */}
      {showRegenConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setShowRegenConfirm(false)}>
          <div onClick={e => e.stopPropagation()} data-testid="regen-confirm-modal" style={{ background: theme === 'light' ? '#fff' : '#1a1917', border: `1px solid ${t.border}`, padding: '32px', maxWidth: '420px', width: '90%', boxShadow: '0 40px 100px rgba(0,0,0,.5)', borderRadius: '12px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Sparkles size={28} style={{ color: t.gold, margin: '0 auto 16px' }} />
              <h3 style={{ fontFamily: 'var(--serif)', fontSize: '22px', marginBottom: '8px', color: t.fg }}>Generate a New Look</h3>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: t.muted, lineHeight: 1.6 }}>
                Describe the direction you want, or leave blank for a completely fresh random design.
              </p>
            </div>
            <input
              type="text"
              value={regenDirection}
              onChange={e => setRegenDirection(e.target.value)}
              placeholder="e.g. darker and more cinematic, or minimal with lots of whitespace"
              data-testid="regen-direction-input"
              style={{ width: '100%', padding: '12px 14px', fontSize: '14px', fontFamily: 'var(--sans)', background: t.inputBg, border: `1px solid ${t.border}`, color: t.fg, outline: 'none', borderRadius: '10px', marginBottom: '16px' }}
              onKeyDown={e => { if (e.key === 'Enter') handleRegenerate(); }}
              autoFocus
            />
            <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: t.muted, marginBottom: '20px', lineHeight: 1.5 }}>
              Your current version is saved — you can <strong style={{ color: t.fg }}>Undo</strong> to get it back.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowRegenConfirm(false); setRegenDirection(''); }} style={{ flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, background: 'transparent', border: `1px solid ${t.border}`, color: t.fg, cursor: 'pointer', borderRadius: '10px' }}>
                Cancel
              </button>
              <button onClick={handleRegenerate} data-testid="regen-confirm-btn" style={{ flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 600, background: t.gold, border: 'none', color: theme === 'light' ? '#fff' : '#000', cursor: 'pointer', borderRadius: '10px', boxShadow: '0 2px 8px rgba(201,168,76,0.2)' }}>
                {regenDirection.trim() ? 'Generate This' : 'Surprise Me'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BuilderPage;
